import _ from 'lodash';
import {
    all,
    call,
    cancel,
    fork,
    put,
    select,
    take,
    takeLatest
} from 'redux-saga/effects';
import { OPEN_ERROR_MODAL } from '../iwModals/Modals.actions';
import {
    getCacheKeyFromAction,
    getCurrentStateKey,
    storeResponseInReactQuery
} from '../iwQueryClient/iwQueryClient';
import { isErrorResponse } from './HelperAPI';
import { lendingAPISagaWorkers } from './lendingAPI/LendingAPI.sagas';
import { LendingAPIError } from './lendingAPI/LendingAPIError';

export const apiSagaWorkers = [...lendingAPISagaWorkers];

export function forkLatestAPI(apiAction, fetchMethod) {
    return fork(function* () {
        yield takeLatest(apiAction, createAPIWorker(fetchMethod));
    });
}

function createAPIWorker(fetchMethod) {
    return function* fetchAPI(action) {
        try {
            yield put({
                type: `${action.type} [pending]`,
                response: {
                    pending: true,
                    data: {}
                }
            });

            const response = yield callLendingAPI(fetchMethod, action);

            syncReactQueryState({
                actionName: action.type,
                response: response
            });

            yield put({
                type: `${action.type} [success]`,
                response: {
                    pending: false,
                    ...response.data
                }
            });
        } catch (errorResponse) {
            yield put({
                type: `${action.type} [fail]`,
                response: {
                    pending: false,
                    ...errorResponse.data
                }
            });
        }
    };
}

function* callLendingAPI(fetchMethod, action) {
    const impersonatingStateKey = yield select(
        ({ history }) => history.impersonatingStateKey
    );
    const result = yield call(fetchMethod, {
        ...action,
        impersonatingStateKey
    });
    const response = { data: result[0], meta: result[1] };

    if (isErrorResponse(response.meta)) {
        throw new LendingAPIError(response);
    } else {
        return response;
    }
}

function syncReactQueryState({ actionName, response }) {
    const stateKey = getCurrentStateKey();
    const cacheKey = getCacheKeyFromAction({
        actionName: actionName,
        queryParams: [stateKey]
    });
    storeResponseInReactQuery({
        cacheKey: cacheKey,
        response: response.data
    });
}

export function* putAndHandleAPIError(actionAPI) {
    yield put(actionAPI);

    const response = yield take([
        `${actionAPI.type} [success]`,
        `${actionAPI.type} [fail]`,
        `${actionAPI.type} [permissionDenied]`
    ]);

    if (response.type === `${actionAPI.type} [success]`) return response;
    if (response.type === `${actionAPI.type} [permissionDenied]`) return;

    yield put({
        type: OPEN_ERROR_MODAL,
        id: 'FallbackErrorModal'
    });

    yield cancel();
}

export function* putAndWaitHandleAllAPIError(actionAPIs) {
    const resolvedAPIRequests = yield putAndWaitAllAPI(actionAPIs);

    const failedRequests = _.filter(resolvedAPIRequests, (resolvedRequest) =>
        resolvedRequest.type.includes('[fail]')
    );

    if (failedRequests.length === 0) return resolvedAPIRequests;

    yield put({
        type: OPEN_ERROR_MODAL,
        id: 'FallbackErrorModal'
    });

    yield cancel();
}

export function* putAndWaitAPI(actionAPI) {
    yield put(actionAPI);

    return yield take([
        `${actionAPI.type} [success]`,
        `${actionAPI.type} [fail]`
    ]);
}

export function* putAndWaitAllAPI(actionAPIs) {
    const putAPIs = _.map(actionAPIs, (actionAPI) => put(actionAPI));
    const takeAPIs = _.map(actionAPIs, (actionAPI) =>
        take([
            `${actionAPI.type} [success]`,
            `${actionAPI.type} [fail]`,
            `${actionAPI.type} [permissionDenied]`
        ])
    );

    yield all(putAPIs);
    return yield all(takeAPIs);
}

export function* selectAPI() {
    return yield select(({ api }) => {
        return _.mapValues(api, 'data');
    });
}
